<template lang="pug">
div
  v-row.mb-4
    v-col(cols="12" sm="4")
      comparison-card(title="Average in-clinic time" averageTitle="Average in-clinic time comparison" subject="Average in-clinic time" :todayValue="stats.avg_total_min || 0" :yesterdayValue="prevStats.avg_total_min || 0" category="in-clinic" :loading="isFetchingTrends" :period="currentDateRange" type="time")

    v-col(cols="12" sm="8")
      chart-card(:loading="isFetchingTrends" title="In-clinic time trend" :overview="totalOverview" :chartLabels="categories" :chartValues="totalPoints" :timeframe="timeframe" type="time" :currentDateRange="currentDateRange" :isToday="isToday")

    v-col(cols="12" sm="4")
      comparison-card(title="Average waiting time" averageTitle="Average waiting time comparison" subject="Average waiting time" :todayValue="stats.avg_waiting_min || 0" :yesterdayValue="prevStats.avg_waiting_min || 0" category="in-clinic" :loading="isFetchingTrends" :period="currentDateRange" type="time")

    v-col(cols="12" sm="8")
      chart-card(:loading="isFetchingTrends" title="Waiting time trend" :overview="waitingOverview" :chartLabels="categories" :chartValues="waitingPoints" :timeframe="timeframe" type="time" :currentDateRange="currentDateRange" :isToday="isToday")

    v-col(cols="12" sm="4")
      comparison-card(title="Average serving time" averageTitle="Average serving time comparison" subject="Average serving time" :todayValue="stats.avg_serving_min || 0" :yesterdayValue="prevStats.avg_serving_min || 0" category="in-clinic" :loading="isFetchingTrends" :period="currentDateRange" type="time")

    v-col(cols="12" sm="8")
      chart-card(:loading="isFetchingTrends" title="Serving time trend" :overview="servingOverview" :chartLabels="categories" :chartValues="servingPoints" :timeframe="timeframe" type="time" :currentDateRange="currentDateRange" :isToday="isToday")

    template(v-if="!isToday")
      v-col(cols="12" sm="12")
        heat-map-card(:data="occupancy" title="Doctor occupancy" description="Shows your doctor's peak hours, including both walk-in and appointment patients")

      v-col(cols="12" sm="6")
        chart-card(:loading="isFetchingDailyTrends" title="Average daily patients" :chartLabels="dailyLabels" :chartValues="dailyValues" :timeframe="timeframe" type="custom" :currentDateRange="currentDateRange" :isToday="isToday")

      v-col(cols="12" sm="6")
        chart-card(:loading="isFetchingHourlyTrends" title="Average hourly patients" :chartLabels="hourlyLabels" :chartValues="hourlyValues" :timeframe="timeframe" type="custom" :currentDateRange="currentDateRange" :isToday="isToday")

      v-col(cols="12" sm="6")
        pie-chart-card(:loading="isFetchingSegments" title="Urgent vs normal patient" type="inclinic" :data="urgencyData")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { rangeValueFromPeriod } from '@/utils'
import ComparisonCard from './ComparisonCard.vue'
import StatCard from './StatCard.vue'
import ChartCard from './ChartCard.vue'
import HeatMapCard from './HeatMapCard.vue'
import PieChartCard from './PieChartCard.vue'

export default {
  name: 'InClinicSection',
  components: { ComparisonCard, StatCard, ChartCard, HeatMapCard, PieChartCard },
  props: {
    isToday: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isFetchingTrends: true,
    isFetchingDailyTrends: true,
    isFetchingHourlyTrends: true,
    isFetchingOccupancy: true,
    isFetchingSegments: true,
    timeframe: null,
    trends: {},
    stats: {},
    prevStats: {},
    occupancy: {},
    urgencyData: {},
    categories: [],
    totalPoints: [],
    servingPoints: [],
    waitingPoints: [],
    dailyValues: [],
    dailyLabels: [],
    hourlyValues: [],
    hourlyLabels: [],
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
      dates: 'insight/getDates',
      beginDate: 'insight/getBeginDate',
      untilDate: 'insight/getUntilDate',
      selectedProviders: 'insight/getSelectedProviders',
      selectedCompanies: 'insight/getSelectedCompanies',
      currentDateRange: 'insight/getCurrentDateRange',
      isHq: 'hq/getIsHq'
    }),
    overview () {
      return this.trends.trend?.overview ?? {}
    },
    totalOverview () {
      return {
        min: this.overview.min_total_min || 0,
        max: this.overview.max_total_min || 0,
        avg: this.overview.avg_total_min || 0,
      }
    },
    waitingOverview () {
      return {
        min: this.overview.min_waiting_min || 0,
        max: this.overview.max_waiting_min || 0,
        avg: this.overview.avg_waiting_min || 0,
      }
    },
    servingOverview () {
      return {
        min: this.overview.min_serving_min || 0,
        max: this.overview.max_serving_min || 0,
        avg: this.overview.avg_serving_min || 0,
      }
    },
  },
  watch: {
    dates () {
      this.fetchData()
    },
    selectedProviders: {
      handler() {
        this.fetchData()
      },
      deep: true
    },
    selectedCompanies: {
      handler() {
        this.fetchData()
      },
      deep: true
    }
  },
  mounted () {
    this.fetchData()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchData() {
      this.fetchTrends()
      this.fetchDailyTrends()
      this.fetchHourlyTrends()
      this.fetchOccupancy()
      this.fetchSegments()
    },
    fetchTrends (stopRefetch) {
      this.isFetchingTrends = true
      const params = {}
      if (this.isHq) {
        if (this.selectedCompanies) {
          params.companies = this.selectedCompanies.map(c => c.id).join(',')
        } else {
          params.hq = this.company.id
        }
      } else {
        params.company = this.company.id
      }
      if (this.isToday) {
        params.range = 'today'
      } else {
        params.begin = this.beginDate
        params.until = this.untilDate
        params.range = rangeValueFromPeriod(this.currentDateRange?.id)
      }
      if (!this.isToday && this.selectedProviders && this.selectedProviders.length > 0) {
        params.providers = this.selectedProviders.map(p => p.id).join(',')
      }
      this.$axios.get(`/trends/${this.company.slug}/registrations`, { params })
        .then(resp => {
          const data = resp.data
          this.trends = data
          this.stats = data.stats
          this.prevStats = data.prev_stats
          this.updateChart()
          this.isFetchingTrends = false
        })
        .catch(() => {
          if (!stopRefetch) {
            this.fetchTrends(true)
          } else {
            this.isFetchingTrends = false
          }
        })
    },
    fetchDailyTrends (stopRefetch) {
      this.isFetchingDailyTrends = true
      const params = {}
      if (this.isHq) {
        if (this.selectedCompanies) {
          params.companies = this.selectedCompanies.map(c => c.id).join(',')
        } else {
          params.hq = this.company.id
        }
      } else {
        params.company = this.company.id
      }
      if (this.isToday) {
        params.range = 'today'
      } else {
        params.begin = this.beginDate
        params.until = this.untilDate
        params.range = rangeValueFromPeriod(this.currentDateRange?.id)
      }
      if (!this.isToday && this.selectedProviders && this.selectedProviders.length > 0) {
        params.providers = this.selectedProviders.map(p => p.id).join(',')
      }
      this.$axios.get(`/trends/${this.company.slug}/registrations/daily`, { params })
        .then(resp => {
          const stats = resp.data.stats
          this.dailyLabels = stats.map(x => x.day)
          this.dailyValues = stats.map(x => x.avg)
          this.isFetchingDailyTrends = false
        })
        .catch(() => {
          if (!stopRefetch) {
            this.fetchDailyTrends(true)
          } else {
            this.isFetchingDailyTrends = false
          }
        })
    },
    fetchHourlyTrends (stopRefetch) {
      this.isFetchingHourlyTrends = true
      const params = {}
      if (this.isHq) {
        if (this.selectedCompanies) {
          params.companies = this.selectedCompanies.map(c => c.id).join(',')
        } else {
          params.hq = this.company.id
        }
      } else {
        params.company = this.company.id
      }
      if (this.isToday) {
        params.range = 'today'
      } else {
        params.begin = this.beginDate
        params.until = this.untilDate
        params.range = rangeValueFromPeriod(this.currentDateRange?.id)
      }
      if (!this.isToday && this.selectedProviders && this.selectedProviders.length > 0) {
        params.providers = this.selectedProviders.map(p => p.id).join(',')
      }
      this.$axios.get(`/trends/${this.company.slug}/registrations/hourly`, { params })
        .then(resp => {
          const stats = resp.data.stats
          this.hourlyLabels = stats.map(x => x.hour)
          this.hourlyValues = stats.map(x => x.avg)
          this.isFetchingHourlyTrends = false
        })
        .catch(() => {
          if (!stopRefetch) {
            this.fetchHourlyTrends(true)
          } else {
            this.isFetchingHourlyTrends = false
          }
        })
    },
    fetchOccupancy (stopRefetch) {
      this.isFetchingOccupancy = true
      const params = {}
      if (this.isHq) {
        if (this.selectedCompanies) {
          params.companies = this.selectedCompanies.map(c => c.id).join(',')
        } else {
          params.hq = this.company.id
        }
      } else {
        params.company = this.company.id
      }
      if (this.isToday) {
        params.range = 'today'
      } else {
        params.begin = this.beginDate
        params.until = this.untilDate
        params.range = rangeValueFromPeriod(this.currentDateRange?.id)
      }
      if (!this.isToday && this.selectedProviders && this.selectedProviders.length > 0) {
        params.providers = this.selectedProviders.map(p => p.id).join(',')
      }
      this.$axios.get(`/trends/${this.company.slug}/registrations/occupancy`, { params })
        .then(resp => {
          this.occupancy = resp.data
          this.isFetchingOccupancy = false
        })
        .catch(() => {
          if (!stopRefetch) {
            this.fetchOccupancy(true)
          } else {
            this.isFetchingOccupancy = false
          }
        })
    },
    fetchSegments (stopRefetch) {
      this.isFetchingSegments = true
      const params = {}
      if (this.isHq) {
        if (this.selectedCompanies) {
          params.companies = this.selectedCompanies.map(c => c.id).join(',')
        } else {
          params.hq = this.company.id
        }
      } else {
        params.company = this.company.id
      }
      if (this.isToday) {
        params.range = 'today'
      } else {
        params.begin = this.beginDate
        params.until = this.untilDate
        params.range = rangeValueFromPeriod(this.currentDateRange?.id)
      }
      if (!this.isToday && this.selectedProviders && this.selectedProviders.length > 0) {
        params.providers = this.selectedProviders.map(p => p.id).join(',')
      }
      this.$axios.get(`/trends/${this.company.slug}/registrations/segments`, { params })
        .then(resp => {
          const urgency = resp.data?.urgency
          if (urgency) {
            this.urgencyData = {
              labels: ['Urgent', 'Normal patient'],
              values: [urgency.urgent_reg, urgency.normal_reg],
            }
          }
          this.isFetchingSegments = false
        })
        .catch(() => {
          if (!stopRefetch) {
            this.fetchSegments(true)
          } else {
            this.isFetchingSegments = false
          }
        })
    },
    updateChart () {
      const trend = this.trends.trend
      this.categories = trend.points.map(x => x.point)
      this.totalPoints = trend.points.map(i => i.data.total_min)
      this.waitingPoints = trend.points.map(i => i.data.waiting_min)
      this.servingPoints = trend.points.map(i => i.data.serving_min)
      this.timeframe = trend.timeframe
    },
  }
}
</script>